import type {PropertyValuesRecord, RepoSettingsPropertiesPagePayload} from '@github-ui/custom-properties-types'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useNavigate} from '@github-ui/use-navigate'
import {PencilIcon} from '@primer/octicons-react'
import {Box, Button} from '@primer/react'
import {useMemo, useState} from 'react'

import {EditRepoPropertiesDialog} from '../components/EditRepoPropertiesDialog'
import {PageHeader} from '../components/PageHeader'
import {RepoPropertiesList} from '../components/RepoPropertiesList'
import {CurrentOrgRepoProvider} from '../contexts/CurrentOrgRepoContext'
import {useSetFlash} from '../contexts/FlashContext'

export function RepoSettingsCustomPropertiesPage() {
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const setFlash = useSetFlash()
  const navigate = useNavigate()
  const {
    definitions,
    currentRepo,
    editableProperties: editablePropertyNames,
  } = useRoutePayload<RepoSettingsPropertiesPagePayload>()

  const editablePropertyNamesSet = useMemo(() => new Set(editablePropertyNames), [editablePropertyNames])
  const editableDefinitions = useMemo(
    () => definitions.filter(({propertyName}) => editablePropertyNamesSet.has(propertyName)),
    [definitions, editablePropertyNamesSet],
  )
  const editableProperties = useMemo(() => {
    const values: PropertyValuesRecord = currentRepo.properties || {}
    return editableDefinitions.reduce<PropertyValuesRecord>(
      (acc, {propertyName}) => Object.assign(acc, {[propertyName]: values[propertyName]}),
      {},
    )
  }, [editableDefinitions, currentRepo])

  const canEdit = Object.keys(editableProperties).length > 0
  const onEditClick = () => {
    setEditDialogOpen(true)
    setFlash(null)
  }

  return (
    <CurrentOrgRepoProvider>
      <Header canEdit={canEdit} onEditClick={onEditClick} />

      <RepoPropertiesList definitions={definitions} values={currentRepo.properties || {}} />

      {editDialogOpen && (
        <EditRepoPropertiesDialog
          editableProperties={editablePropertyNames}
          definitions={definitions}
          editingRepos={[currentRepo]}
          onDismiss={() => setEditDialogOpen(false)}
          onSuccess={() => {
            setEditDialogOpen(false)
            setFlash('repos.properties.updated')
            navigate(window.location)
          }}
        />
      )}
    </CurrentOrgRepoProvider>
  )
}

export function Header({canEdit, onEditClick}: {canEdit: boolean; onEditClick?: () => void}) {
  return (
    <PageHeader
      title={
        <Box as="h2" sx={{fontWeight: 400}}>
          Custom properties
        </Box>
      }
      subtitle="Custom properties allow you to decorate your repository with information such as compliance frameworks, data sensitivity, or project details."
      actions={
        <>
          {canEdit && (
            <Button variant={'primary'} leadingVisual={PencilIcon} onClick={onEditClick}>
              Edit
            </Button>
          )}
        </>
      }
    />
  )
}

try{ RepoSettingsCustomPropertiesPage.displayName ||= 'RepoSettingsCustomPropertiesPage' } catch {}
try{ Header.displayName ||= 'Header' } catch {}